(ns minorodata.components.analytics-models.overview
  "Read-only overview of an analytics-model instance — its operational state
   (status, schedule), I/O tables, and recent run logs. Editing is a separate
   screen reached via the Edit action in the panel header."
  (:require [clojure.string :as str]
            [minorodata.analytics-models.domain :as domain]
            [minorodata.app.utils :as app.utils]
            [minorodata.components.analytics-models.schema :as schema]
            [minorodata.components.analytics-models.run-status :refer [run-status-pill]]
            [minorodata.components.pipeline-editor.details.analytics-models :refer [recent-logs]]
            [re-frame.core :as re-frame]
            [ui.card :as ui.card]
            [ui.date-time :refer [date-tooltip]]
            [ui.tabs :as ui.tabs]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))


(defn status-badge [status]
  (let [active? (= status "active")]
    [:span {:class (clsx "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                         (if active?
                           "bg-kleene-success-background text-kleene-green"
                           "bg-kleene-background-secondary text-kleene-text-secondary"))}
     [:span {:class (clsx "h-1.5 w-1.5 rounded-full" (if active? "bg-kleene-green" "bg-kleene-text/40"))}]
     (if active? "Active" "Paused")]))


(defn- stat [label value]
  [ui.card/card
   [ui.card/card-content {:class "flex flex-col gap-1.5"}
    [typography/label {:class "text-kleene-text-secondary"} label]
    value]])


(defn- params-summary [{:keys [instance model]}]
  (let [params (:analytics-model/model-params instance)
        blocks (:analytics-model-registry/custom-ui-blocks model)]
    (when (seq blocks)
      [:div {:class "flex flex-col gap-2"}
       [typography/h3 "Parameters"]
       [ui.card/card
        [ui.card/card-content {:class "flex flex-col divide-y divide-kleene-layout-border"}
         (for [{:keys [label] :as block} blocks
               :let [pname (domain/mapping-key (:name block))
                     value (get params pname)]]
           ^{:key pname}
           [:div {:class "flex items-center justify-between gap-4 py-2 first:pt-0 last:pb-0"}

            [typography/label {:class "text-kleene-text-secondary"} (or label (name pname))]
            [:span {:class "text-sm"} (if (some? value) (str value) "—")]])]]])))


(defn- summary-tab [{:keys [instance model]}]
  (let [{:analytics-model/keys [model-id status schedule-spec updated-at description]} instance
        {:keys [input-tables output-tables]} (:analytics-model-registry/table-definitions model)
        run-status @(re-frame/subscribe [:analytics-models.subs/last-run-status model-id])
        schedule (app.utils/format-schedule {:cron            (:cron schedule-spec)
                                             :empty-status    "Not scheduled"
                                             :required-fields [:minute]})]
    [:div {:class "flex flex-col gap-6"}
     (when-not (str/blank? description)
       [typography/p {:class "text-kleene-text-secondary"} description])

     [:div {:class "grid grid-cols-1 md:grid-cols-3 gap-3"}
      [stat "Status" [status-badge status]]
      [stat "Last run" [run-status-pill run-status {:show-time? true}]]
      [stat "Schedule" [:span {:class "text-sm"} schedule]]
      [stat "Last updated"
       [:span {:class "text-sm"}
        (if updated-at [date-tooltip {:date updated-at}] "—")]]]

     [params-summary {:instance instance :model model}]

     (when (or (seq input-tables) (seq output-tables))
       [:div {:class "flex flex-col gap-2"}
        [typography/h3 "Inputs & outputs"]
        [schema/schema-panel {:model model :instance instance :read-only? true}]])]))


(defn overview [{:keys [instance model]}]
  [ui.tabs/tabs-root {:default-value "overview"}
   [ui.tabs/tabs-list {:variant :underlined}
    [ui.tabs/tabs-trigger {:value "overview" :variant :underlined
                           :data-cy "analytics-model-overview-tab"} "Overview"]
    [ui.tabs/tabs-trigger {:value "logs" :variant :underlined
                           :data-cy "analytics-model-logs-tab"} "Logs"]]
   [ui.tabs/tabs-content {:value "overview" :class "pt-4"}
    [summary-tab {:instance instance :model model}]]
   [ui.tabs/tabs-content {:value "logs" :class "pt-4"}
    [recent-logs {:instance instance}]]])
